/**
 * 商品相关类型定义
 * 从 Gemini types.ts 迁移
 */

/** 商品类型枚举（区分实物商品与 AI 商品） */
export enum ProductType {
  /** 实物商品 */
  Physical = 1,
  /** AI 商品 */
  AI = 2,
}

/** 商品描述字段类型 - 与接口字段 type 对应 */
export enum MetaFieldType {
  /** 纯文本 */
  Text = 1,
  /** 图片 */
  Image = 2,
  /** 富文本 HTML */
  RichText = 3,
}

/** 商品描述项 */
export interface IMetaDescription {
  /** 描述项ID */
  id: string;
  /** 描述类型 */
  type: MetaFieldType;
  /** 描述名称 */
  name: string;
  /** 描述值：文本/富文本为字符串，图片为URL字符串数组 */
  value: string | string[];
}

/** 商品参数值 - 可能是对象或字符串 */
export type IMetaParameterValue = { id: string; value: string } | string;

/** 商品参数项 */
export interface IMetaParameter {
  /** 参数项ID */
  id: string;
  /** 参数类型 */
  type: MetaFieldType;
  /** 参数名称 */
  name: string;
  /** 参数值 */
  value: IMetaParameterValue;
}

/** 商品图片项 */
export interface ProductImage {
  /** 图片ID */
  id: number;
  /** 图片URL */
  url: string;
}

/** 规格维度（名称 + 可选值列表） */
export interface SpecDimension {
  /** 规格名称，如：颜色、型号 */
  name: string;
  /** 规格可选值列表 */
  values: string[];
}

/** 商品 SKU（真实存在的库存单位） */
export interface ProductSKU {
  /** SKU 唯一标识（数字类型，来自后端） */
  id: number;
  /** SKU 编码 */
  code: string;
  /** 货号编号 */
  articleNumber: string;
  /** SKU 价格 */
  price: number;
  /** 库存数量 */
  stock: number;
  /** SKU 图片 URL */
  image?: string;
  /** 建议零售价（与详情 SKU 对齐，v1.3.1） */
  retailPrice?: number;
  /** 第一级规格值（如：红色） */
  spec1Value: string;
  /** 第二级规格值（如：iPhone 15 Pro） */
  spec2Value: string;
  /** SKU 关联的 AI 应用类型码 */
  aiAppConfigTypeCode?: number;
}

/** 商品信息 */
export interface Product {
  /** spu货号 */
  article_number: string;
  /** 商品唯一标识（当前业务中为 SPU 编码） */
  id: string;
  /** 后端商品数值 ID，用于优惠券等需要 product_id 的接口 */
  productId?: number;
  /** 商品类型 */
  productType?: ProductType;
  /** SPU 绑定的物流模板 ID，0 表示未绑定 */
  logisticsDeliveryId?: number;
  /** 商品标题 */
  title: string;
  /** 商品描述 */
  description: string;
  /** 进货价（基础价格/最低价） */
  basePrice: number;
  /** 最高进货价 */
  maxBasePrice?: number;
  /** 最低建议零售价 */
  msrp: number;
  /** 最高建议零售价 */
  maxMsrp?: number;
  /** 商品图片列表 */
  images: ProductImage[];
  /** 分类名称 */
  category: string;
  /** 子分类名称 */
  subCategory?: string;
  /** 商品货号 */
  code: string;
  /** 第一级规格（如：颜色） */
  spec1: SpecDimension;
  /** 第二级规格（如：型号） */
  spec2: SpecDimension;
  /** SKU 列表（只包含真实存在的 SKU） */
  skus: ProductSKU[];
  /** 是否新品 */
  isNew?: boolean;
  /** 是否推荐 */
  isRecommended?: boolean;
  /** 商品描述元字段列表 */
  metaDescription?: IMetaDescription[];
  /** 商品参数元字段列表 */
  metaParameter?: IMetaParameter[];
  /** 服务信息列表 */
  services?: ProductService[];
  /** 是否为单一款式商品（无规格选项，只有一个SKU） */
  isSingleStyle?: boolean;
  /**
   * 款式模式：
   * - 'none': 无规格（styles为空），直接显示数量控件
   * - 'single': 单规格（styles只有一组），显示规格按钮+数量控件
   * - 'multi': 多规格（styles有多组），显示两级规格选择
   */
  styleMode: "none" | "single" | "multi";
  /** C 端销量展示文案（已格式化，无则不下发） */
  salesLabel?: string | null;
  /** 登录用户是否已收藏 */
  isFavorited?: boolean;
  /** 商品标记（v1.3.1） */
  productMarks?: ProductMarkLite[];
  /** 商品优势说明（v1.3.1） */
  advantageDescriptions?: string[];
  /** AI 应用类型码，用于 AI 商品定制入口分流 */
  aiAppConfigTypeCode?: number;
  /** 价格前缀：1-正常价格，2-预估价格 */
  estimatedPriceLabelStatus?: 1 | 2;
  /** 商品扩展配置 */
  extra?: { effect_image_custom?: number };
}

/** 商品服务信息 */
export interface ProductService {
  /** 服务名称 */
  name: string;
  /** 服务描述 */
  desc: string;
  /** 服务图标 */
  serviceIcon?: string;
}

/** 子分类信息 */
export interface SubCategory {
  /** 子分类ID */
  id: number;
  /** 子分类名称 */
  name: string;
  /** 更深层级的子分类 */
  sub?: SubCategory[];
}

/** 分类信息 (组件使用的扁平结构) */
export interface Category {
  /** 分类ID */
  id: number;
  /** 分类名称 */
  name: string;
  /** 子分类列表 */
  sub: SubCategory[];
  /** 分类父ID */
  parent_id: number;
  /** 分类级别 */
  level: number;
  /** 分类简码 */
  code: string;
  /** 分类描述 */
  description: string;
}

/** 分类树节点 (API 返回的树形结构) */
export interface CategoryTreeItem {
  /** 分类ID */
  id: number;
  /** 分类名称 */
  name: string;
  /** 分类简码 */
  code?: string;
  /** 分类级别 */
  level?: number;
  /** 父分类ID */
  parent_id?: number;
  /** 子分类列表 */
  children?: CategoryTreeItem[];
}

/** 分类树列表响应 */
export interface CategoryTreeListResponse {
  /** 分类树列表 */
  list: CategoryTreeItem[];
}

/** 商品标记（列表/详情展示，v1.3.1） */
export interface ProductMarkLite {
  id: number;
  name: string;
  color: string;
}

/** 商品SPU列表请求参数 */
export interface ProductSPUListReq {
  /** 分类ID */
  category_ids?: Array<number>;
  /** 搜索关键词 */
  keyword?: string;
  /** 页码 */
  page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 排序：price_asc / price_desc / sales_asc / sales_desc，空为默认 */
  sort_by?: string;
  /** 服务ID列表（可选，多选OR逻辑） */
  service_ids?: number[];
  /** 按商品标记 ID 筛选（v1.3.1，与后台商品标记管理一致） */
  marker_ids?: number[];
}

/** 商品SPU列表项 (API 返回结构) */
export interface ProductSPUItem {
  /** 货号 */
  article_number: string;
  /** 商品ID */
  id: number;
  /** 商品名称 */
  name: string;
  /** 主图URL */
  image_url: string;
  /** 第二张图URL */
  second_image_url?: string;
  /** SPU编码 */
  spu: string;
  /** 分类ID */
  category_id: number;
  /** 最低价 */
  min_price: number;
  /** 最高价 */
  max_price: number;
  /** 价格前缀：1-正常价格，2-预估价格 */
  estimated_price_label_status?: 1 | 2;
  /** 状态 */
  status: number;
  /** 商品素材 */
  material?: string;
  /** 商品服务列表 */
  services?: Array<{ id: number; name: string; desc: string }>;
  /** 商品类型 */
  product_type?: ProductType;
  /** 真实销量 */
  real_sales?: number;
  /** C 端是否显示销量（1:显示, 2:不显示） */
  is_visible?: number;
  /** 商品标记（v1.3.1） */
  markers?: ProductMarkLite[];
  /** 商品优势说明（v1.3.1） */
  advantages?: string[];
  /** 登录用户是否已收藏（接口可选） */
  is_favorited?: boolean;
}

/** 商品服务项（服务列表接口返回） */
export interface ProductServiceListItem {
  /** 服务ID */
  id: number;
  /** 服务名称 */
  name: string;
  /** 服务描述 */
  desc: string;
  /** 服务图标URL */
  service_icon: string;
}

/** 商品服务列表响应 */
export interface ProductServiceListResponse {
  list: ProductServiceListItem[];
}

/** 推荐商品项（首页推荐接口返回） */
export interface RecommendProductItem {
  /** 货号 */
  article_number: string;
  /** 商品主图（优先低分辨率） */
  image_url: string;
  /** 最高供货价 */
  max_price: number;
  /** 最低供货价 */
  min_price: number;
  /** 商品名称 */
  name: string;
  /** 商品ID */
  product_id: number;
  /** SPU编码 */
  spu: string;
  /** 商品类型 */
  product_type?: ProductType;
  /** 真实销量 */
  real_sales?: number;
  /** C 端是否显示销量（1:显示, 2:不显示） */
  is_visible?: number;
  /** 商品服务列表 */
  services?: Array<{ id: number; name: string; desc: string }>;
  /** 商品标记（v1.3.1） */
  markers?: ProductMarkLite[];
  /** 商品优势说明（v1.3.1） */
  advantages?: string[];
}

/** 推荐商品列表响应 */
export interface RecommendProductListResponse {
  list: RecommendProductItem[];
}

/** 商品SPU列表响应 */
export interface ProductSPUListResponse {
  /** 商品列表 */
  list: ProductSPUItem[];
  /** 页码 */
  page: number;
  /** 每页数量 */
  page_size: number;
  /** 总数 */
  total: number;
}
